import { useState, useEffect } from 'react';
import { useFormationMode } from '@/contexts/FormationModeContext';

export default function SplashScreen() {
  const { mode: currentMode, setMode, dismissSplash, showSplash } = useFormationMode();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [hoveredSide, setHoveredSide] = useState<'left' | 'right' | null>(null);
  const [typedText, setTypedText] = useState('');
  const fullTitle = 'Choisissez votre expérience de formation';
  const hasStoredMode = currentMode !== null;

  useEffect(() => {
    if (showSplash && !dismissed) {
      const t = setTimeout(() => setVisible(true), 200);
      return () => clearTimeout(t);
    }
  }, [showSplash, dismissed]);

  useEffect(() => {
    if (!visible) return;
    let i = 0;
    const interval = setInterval(() => {
      setTypedText(fullTitle.slice(0, i + 1));
      i++;
      if (i >= fullTitle.length) clearInterval(interval);
    }, 40);
    return () => clearInterval(interval);
  }, [visible]);

  if (dismissed) return null;

  const handleChoice = (choice: 'en_ligne' | 'presentiel') => {
    setExiting(true);
    setTimeout(() => {
      setMode(choice);
      // Après la transition CSS (700ms), supprimer l'overlay
      setTimeout(() => setDismissed(true), 750);
    }, 600);
  };

  const handleSkip = () => {
    setExiting(true);
    setTimeout(() => {
      dismissSplash();
      // Après la transition CSS (700ms), supprimer l'overlay
      setTimeout(() => setDismissed(true), 750);
    }, 600);
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex transition-all duration-700 ease-in-out ${
        exiting ? 'opacity-0 scale-105 blur-md' : 'opacity-100 scale-100 blur-0'
      }`}
    >
      {/* Close / Skip button */}
      <button
        onClick={handleSkip}
        className="absolute top-6 right-6 z-30 w-11 h-11 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white hover:bg-white/40 transition-all duration-300 cursor-pointer group"
        aria-label="Fermer"
      >
        <i className="ri-close-line text-xl group-hover:rotate-90 transition-transform duration-300"></i>
      </button>

      {/* Left Panel — En Ligne */}
      <div
        onMouseEnter={() => setHoveredSide('left')}
        onMouseLeave={() => setHoveredSide(null)}
        onClick={() => handleChoice('en_ligne')}
        className={`relative flex-1 flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          hoveredSide === 'left' ? 'flex-[1.35]' : hoveredSide === 'right' ? 'flex-[0.65]' : 'flex-1'
        }`}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20bright%20minimalist%20online%20learning%20workspace%20with%20laptop%20on%20wooden%20desk%20natural%20daylight%20through%20large%20windows%20green%20plants%20on%20shelf%20clean%20airy%20interior%20with%20soft%20shadows%20professional%20digital%20education%20environment%20warm%20neutral%20tones%20elegant%20atmosphere&width=960&height=1080&seq=splash-online-02&orientation=portrait"
            alt="Formations en ligne"
            className="w-full h-full object-cover object-top transition-transform duration-1000 ease-out"
            style={{ transform: hoveredSide === 'left' ? 'scale(1.05)' : 'scale(1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F2B45]/85 via-[#0F2B45]/50 to-[#0F2B45]/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-lg">
          <div className={`mb-6 w-20 h-20 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 transition-all duration-500 ${
            hoveredSide === 'left' ? 'scale-110 shadow-2xl shadow-white/20' : ''
          }`}>
            <i className="ri-wifi-line text-4xl text-white"></i>
          </div>

          <div className={`transition-all duration-500 ${
            hoveredSide === 'left' ? 'translate-y-0 opacity-100 scale-105' : 'translate-y-0 opacity-90'
          }`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-md rounded-full text-white/80 text-xs font-semibold mb-4 border border-white/10">
              <i className="ri-global-line"></i>
              <span>100% à distance</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 font-heading leading-tight">
              Formations<br />En Ligne
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-8 max-w-xs">
              Apprenez depuis n'importe où, à votre rythme. Des formations interactives en direct avec nos experts.
            </p>
            <div className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0F2B45] rounded-xl font-bold text-sm hover:bg-[#FF6B35] hover:text-white transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap group/btn">
              <span>Accéder aux formations en ligne</span>
              <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform"></i>
            </div>
            <div className="mt-4 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white/50 text-xs">Cliquez pour choisir</span>
            </div>
          </div>
        </div>

        {/* Decorative line */}
        <div className="absolute right-0 top-[20%] bottom-[20%] w-px bg-gradient-to-b from-transparent via-white/20 to-transparent" />

        {/* Pastille mode déjà choisi */}
        {currentMode === 'en_ligne' && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30 shadow-lg">
            <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-white/90 text-xs font-semibold whitespace-nowrap">Mode sélectionné</span>
          </div>
        )}
      </div>

      {/* Right Panel — Présentiel */}
      <div
        onMouseEnter={() => setHoveredSide('right')}
        onMouseLeave={() => setHoveredSide(null)}
        onClick={() => handleChoice('presentiel')}
        className={`relative flex-1 flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          hoveredSide === 'right' ? 'flex-[1.35]' : hoveredSide === 'left' ? 'flex-[0.65]' : 'flex-1'
        }`}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20bright%20professional%20training%20classroom%20with%20diverse%20adult%20students%20working%20on%20laptops%20natural%20daylight%20through%20floor%20to%20ceiling%20windows%20white%20walls%20minimalist%20design%20collaborative%20workspace%20contemporary%20corporate%20learning%20environment%20warm%20lighting%20professional%20atmosphere%20high%20resolution&width=960&height=1080&seq=splash-presentiel-02&orientation=portrait"
            alt="Formations en présentiel"
            className="w-full h-full object-cover object-top transition-transform duration-1000 ease-out"
            style={{ transform: hoveredSide === 'right' ? 'scale(1.05)' : 'scale(1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B35]/85 via-[#FF6B35]/50 to-[#FF6B35]/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-lg">
          <div className={`mb-6 w-20 h-20 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 transition-all duration-500 ${
            hoveredSide === 'right' ? 'scale-110 shadow-2xl shadow-white/20' : ''
          }`}>
            <i className="ri-building-line text-4xl text-white"></i>
          </div>

          <div className={`transition-all duration-500 ${
            hoveredSide === 'right' ? 'translate-y-0 opacity-100 scale-105' : 'translate-y-0 opacity-90'
          }`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-md rounded-full text-white/80 text-xs font-semibold mb-4 border border-white/10">
              <i className="ri-map-pin-line"></i>
              <span>En salle de classe</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 font-heading leading-tight">
              Formations<br />En Présentiel
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-8 max-w-xs">
              Vivez une expérience immersive dans nos centres équipés à Abidjan et Genève. Échangez en direct avec formateurs et pairs.
            </p>
            <div className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#FF6B35] rounded-xl font-bold text-sm hover:bg-[#0F2B45] hover:text-white transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap group/btn">
              <span>Accéder aux formations en présentiel</span>
              <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform"></i>
            </div>
            <div className="mt-4 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white/50 text-xs">Cliquez pour choisir</span>
            </div>
          </div>
        </div>

        {/* Pastille mode déjà choisi */}
        {currentMode === 'presentiel' && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30 shadow-lg">
            <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-white/90 text-xs font-semibold whitespace-nowrap">Mode sélectionné</span>
          </div>
        )}
      </div>

      {/* Central overlay title */}
      <div
        className={`absolute top-10 left-1/2 -translate-x-1/2 z-20 text-center transition-all duration-700 ${
          visible && !exiting ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}
      >
        <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/15 backdrop-blur-xl rounded-full border border-white/20 shadow-2xl">
          <img
            src="https://static.readdy.ai/image/932fd3ea349c56e93c423c0500da0e8d/9411ef77eea2c8bea4cc3eb8c7cba478.jpeg"
            alt="Helvitech"
            className="h-8 w-auto object-contain"
          />
          <span className="text-white/40 font-light">×</span>
          <img
            src="https://static.readdy.ai/image/932fd3ea349c56e93c423c0500da0e8d/c1ca3b99bd1a5e423752f5896ff66587.png"
            alt="AIO Consulting"
            className="h-8 w-auto object-contain"
          />
        </div>
        <h1 className="mt-5 text-white text-2xl md:text-3xl font-bold font-heading tracking-tight drop-shadow-lg">
          {typedText}
          <span className="inline-block w-0.5 h-6 bg-white ml-1 animate-pulse align-middle"></span>
        </h1>
      </div>

      {/* Bottom indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-20 transition-all duration-700 ${
        visible && !exiting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        <div className="flex items-center gap-2 text-white/50 text-xs">
          <span>Helvitech × AIO Consulting</span>
          <span className="w-1 h-1 rounded-full bg-white/30"></span>
          <span>Excellence en Formation IT</span>
        </div>
      </div>
    </div>
  );
}